import { useState, type FormEvent } from 'react'
import { ATT_KINDS, localDayKey, type StampKind } from '@wf/shared'
import { ApiError } from '../api/client'
import { STAMP_KIND_LABEL } from '../corrections/labels'
import { localTimeToIso } from './timeMath'
import { useCorrectAttendance } from './useZeiten'

/**
 * Chef-Nachtrag eines fehlenden Stempels (W9.1, revisionssicher): Art + Tag + Uhrzeit + Pflicht-Grund.
 * Der Zeitstempel geht als voller ISO-String mit Zeitzone raus (Handoff-Warnung: nacktes Datum wäre
 * mehrdeutig). Zukunft blockt der Server (>5 min) — die UI begrenzt das date-Input zusätzlich auf heute.
 */
export function AddStampForm({
  userId,
  userName,
  initialDay,
  initialTime,
  initialKind,
  onSuccess,
  onClose,
}: {
  userId: number
  userName: string
  initialDay?: string
  initialTime?: string
  initialKind?: StampKind
  onSuccess?: () => void
  onClose: () => void
}) {
  const today = localDayKey(Date.now())
  const [day, setDay] = useState(initialDay ?? today)
  const [time, setTime] = useState(initialTime ?? '08:00')
  const [kind, setKind] = useState<StampKind>(initialKind ?? 'kommen')
  const [reason, setReason] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const correct = useCorrectAttendance()

  function submit(event: FormEvent) {
    event.preventDefault()
    const ts = localTimeToIso(day, time)
    if (!ts) {
      setLocalError('Bitte Tag und Uhrzeit angeben')
      return
    }
    if (!reason.trim()) {
      setLocalError('Bitte einen Grund angeben (bleibt dauerhaft dokumentiert)')
      return
    }
    setLocalError(null)
    correct.mutate(
      { action: 'add', userId, kind, ts, reason: reason.trim() },
      { onSuccess: () => onSuccess?.() },
    )
  }

  const serverError = correct.error instanceof ApiError ? correct.error.message : correct.error ? 'Speichern fehlgeschlagen.' : null

  return (
    <form className="wf-card" onSubmit={submit} aria-label={`Stempel nachtragen für ${userName}`}>
      <h3 className="wf-sectionhead">Stempel nachtragen · {userName}</h3>
      <div className="wf-chips" role="group" aria-label="Stempelart">
        {ATT_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={kind === k ? 'wf-chip wf-chip--active' : 'wf-chip'}
            aria-pressed={kind === k}
            onClick={() => setKind(k)}
          >
            {STAMP_KIND_LABEL[k]}
          </button>
        ))}
      </div>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Tag</span>
          <input className="wf-input" type="date" max={today} value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
        <label className="wf-field">
          <span className="wf-label">Uhrzeit</span>
          <input className="wf-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
      </div>
      <label className="wf-field">
        <span className="wf-label">Grund (Pflicht, bleibt dokumentiert)</span>
        <input
          className="wf-input"
          type="text"
          placeholder="z. B. Kommen vergessen zu stempeln"
          maxLength={300}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {localError ? (
        <p className="error" role="alert">
          {localError}
        </p>
      ) : null}
      {serverError ? (
        <p className="error" role="alert">
          {serverError}
        </p>
      ) : null}
      <div className="wf-listfoot">
        <button type="button" className="wf-btn wf-btn--ghost" onClick={onClose}>
          Abbrechen
        </button>
        <button type="submit" className="wf-btn wf-btn--primary" disabled={correct.isPending}>
          {correct.isPending ? 'Speichere…' : 'Nachtragen'}
        </button>
      </div>
    </form>
  )
}
